import { useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";
import iconCross from "../../photos/cross.svg";
import { randomTilt } from "../Select.tsx";
import { TOAST_LEAVE_MS } from "../InfoMessage/InfoMessage";
import "../../App.css";
import "../InfoMessage/InfoMessage.css";
import "./PremiumInfo.css";
import { API_BASE } from "../../apiBase";

// The "WHAT IS PREMIUM?" link and the big toast it drops down: a pink sticker
// like the regular toasts, closed with the cross or a tap outside, that can
// turn into a feedback form posting to /api/feedback.
export default function PremiumInfo({ roomName, source, showInfoMessage }) {
  const { t, i18n } = useTranslation();
  const [open, setOpen] = useState(false);
  const [leaving, setLeaving] = useState(false);
  const [view, setView] = useState("info"); // "info" | "feedback"
  const [message, setMessage] = useState("");
  const [sending, setSending] = useState(false);
  // A big card tips half as far as a small sticker, and the other way each time
  const lastTilt = useRef();
  const [tilt, setTilt] = useState("0deg");

  const show = () => {
    lastTilt.current = randomTilt(lastTilt.current);
    setTilt(`${parseFloat(lastTilt.current) / 2}deg`);
    setView("info");
    setLeaving(false);
    setOpen(true);
  };

  // Lift it off the top, then unmount
  const close = () => {
    if (leaving) return;
    setLeaving(true);
    setTimeout(() => {
      setOpen(false);
      setLeaving(false);
    }, TOAST_LEAVE_MS);
  };

  const send = async () => {
    if (!message.trim()) {
      showInfoMessage?.("warning", t("premiumInfo.empty"), false);
      return;
    }
    if (sending) return;
    setSending(true);
    try {
      const res = await fetch(`${API_BASE}/api/feedback`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          message,
          roomName,
          source,
          language: i18n.language,
        }),
      });
      if (!res.ok) throw new Error(`status ${res.status}`);
      setMessage("");
      close();
      showInfoMessage?.("success", t("premiumInfo.sent"), false);
    } catch (error) {
      showInfoMessage?.("warning", t("premiumInfo.failed"), false);
    } finally {
      setSending(false);
    }
  };

  const toast = (
    <>
      <div className="premium-toast-backdrop" onClick={close} />
      <div
        className={`premium-toast toast-sticker${leaving ? " toast-leaving" : ""}`}
        style={{ "--tilt": tilt }}
        role="dialog"
        aria-label={t("premiumInfo.link")}
      >
        <div className="premium-toast-head">
          <div className="text-h3">
            {view === "info" ? t("premiumInfo.link") : t("premiumInfo.feedback")}
          </div>
          <img
            src={iconCross}
            alt="Close Icon"
            className="toast-close"
            onClick={close}
          />
        </div>

        {view === "info" ? (
          <>
            <p className="text-h4 premium-toast-body">
              {t("premiumInfo.body", { start: t("start") })}
            </p>
            <div
              className="premium-toast-send black-fill text-color-pink button-text-s box-button-center"
              style={{ alignSelf: "stretch" }}
              onClick={() => setView("feedback")}
            >
              {t("premiumInfo.feedback")}
            </div>
          </>
        ) : (
          <>
            <textarea
              className="text-h4 premium-toast-field"
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              placeholder={t("write-here")}
              maxLength={2000}
              rows={4}
              autoFocus
            />
            <div className="premium-toast-actions">
              <div
                className="button-tertiary-m button-text-s text-color-black"
                onClick={() => setView("info")}
              >
                {t("back")}
              </div>
              <div
                className="premium-toast-send black-fill text-color-pink button-text-m box-button-center"
                onClick={send}
                style={{ opacity: sending ? 0.5 : 1 }}
              >
                {t("premiumInfo.send")}
              </div>
            </div>
          </>
        )}
      </div>
    </>
  );

  return (
    <>
      <div
        className="button-tertiary-s button-text-s text-color-black bottom-margin-m premium-link"
        onClick={show}
      >
        {t("premiumInfo.link")}
      </div>
      {open && createPortal(toast, document.body)}
    </>
  );
}
